<script>
import { mapState, mapActions } from 'vuex'
import VueApexCharts from 'vue-apexcharts'
import vSelect from 'vue-select'

export default {
   components: {
      VueApexCharts,
      vSelect,
   },
   data() {
      return {
         // b-tabs menggunakan index numerik sebagai nilai v-model.
         // Default tab mengikuti triwulan yang sedang berjalan (real time):
         // Jan-Mar -> Triwulan I, Apr-Jun -> II, Jul-Sep -> III, Okt-Des -> IV.
         activeTab: Math.floor(new Date().getMonth() / 3),
         loading: false,
         instansi: 'DPMPTSP',
         instansiOptions: [
            'SAMSAT',
            'BANK KALBAR',
            'DPMPTSP',
            'BAPPENDA',
            'BPJS Kesehatan',
            'Kejaksaan',
            'POLRES',
            'DPK',
            'BPJS Ketenagakerjaan',
            'DISDUKCAPIL',
            'KPP Pratama',
            'PT PLN',
            'BNN',
            'PT POS',
            'BRI',
            'Kementrian Agama',
            'ATR / BPN',
            'Kementerian Agama',
            'Perumda Air Minum Tirta Senentang',
            'CU Keling Kumang',
            'CU Bima',
         ],
      }
   },
   computed: {
      ...mapState({
         unsurPelayanan: state => state.DashboardSkm.unsurPelayanan,
         tahun: state => state.DashboardSkm.tahun,
         refreshTimestamp: state => state.DashboardSkm.refreshTimestamp,
         skin: state => state.appConfig.layout.skin,
      }),
      isDark() {
         return this.skin === 'dark'
      },
      activeTw() {
         return this.activeTab + 1
      },
      // Response API: [{ unsur, jumlah, nnr }] — diurutkan menurun berdasarkan
      // NNR; ApexCharts menggambar kategori pertama di posisi paling atas,
      // sehingga unsur dengan NNR terbesar selalu tampil paling atas.
      sortedUnsur() {
         const source = this.unsurPelayanan.data

         const items = Array.isArray(source)
            ? source.map(item => ({
               key: (item || {}).unsur,
               label: this.unsurLabel((item || {}).unsur),
               nnr: Number((item || {}).nnr) || 0,
               jumlah: Number((item || {}).jumlah) || 0,
               responden: Number((item || {}).responden) || 0,
            }))
            : Object.keys(source || {}).map(key => ({
               key,
               label: this.unsurLabel(key),
               nnr: Number((source[key] || {}).nnr) || 0,
               jumlah: Number((source[key] || {}).jumlah) || 0,
               responden: Number((source[key] || {}).responden) || 0,
            }))

         return items.sort((a, b) => b.nnr - a.nnr)
      },
      chartSeries() {
         return [{
            name: 'NNR',
            data: this.sortedUnsur.map(item => item.nnr),
         }]
      },
      chartOptions() {
         const textColor = this.isDark ? '#b4b7bd' : '#6e6b7b'

         return {
            chart: {
               type: 'bar',
               foreColor: textColor,
               toolbar: {
                  show: false,
               },
               fontFamily: 'Montserrat, Helvetica, Arial, sans-serif',
            },
            colors: ['#7367f0'],
            plotOptions: {
               bar: {
                  horizontal: true,
                  borderRadius: 4,
                  barHeight: '60%',
                  endingShape: 'rounded',
               },
            },
            dataLabels: {
               enabled: true,
               textAnchor: 'start',
               offsetX: 0,
               formatter: value => Number(value).toFixed(2).replace('.', ','),
               style: {
                  colors: [textColor],
                  fontSize: '12px',
               },
            },
            grid: {
               borderColor: this.isDark ? '#3b4253' : '#ebe9f1',
               strokeDashArray: 4,
            },
            xaxis: {
               categories: this.sortedUnsur.map(item => item.label),
               min: 0,
               max: 4,
               tickAmount: 4,
               title: {
                  text: 'Nilai Rata-rata (NNR)',
                  style: {
                     color: textColor,
                  },
               },
               labels: {
                  style: {
                     colors: textColor,
                  },
                  formatter: value => (typeof value === 'number' ? value.toFixed(2).replace('.', ',') : value),
               },
            },
            yaxis: {
               labels: {
                  style: {
                     colors: textColor,
                  },
                  maxWidth: 240,
               },
            },
            tooltip: {
               theme: this.isDark ? 'dark' : 'light',
               y: {
                  formatter: (value, opts) => {
                     const item = this.sortedUnsur[opts.dataPointIndex] || {}
                     const jumlah = Number(item.jumlah || 0)
                     const responden = Number(item.responden || 0)

                     return `NNR : ${Number(value).toFixed(2).replace('.', ',')} • Total Nilai : ${jumlah.toLocaleString('id-ID')} • Responden : ${responden.toLocaleString('id-ID')}`
                  },
               },
            },
         }
      },
   },
   watch: {
      tahun(newYear, oldYear) {
         if (newYear !== oldYear) {
            this.loadData({ year: newYear, instansi: this.instansi, tw: this.activeTw })
         }
      },
      instansi(newInstansi, oldInstansi) {
         if (newInstansi !== oldInstansi) {
            this.loadData({ year: this.tahun, instansi: newInstansi, tw: this.activeTw })
         }
      },
      activeTab() {
         this.loadData({ year: this.tahun, instansi: this.instansi, tw: this.activeTw })
      },
      refreshTimestamp() {
         this.loadData({ year: this.tahun, instansi: this.instansi, tw: this.activeTw })
      },
   },
   mounted() {
      this.loadData({ year: this.tahun, instansi: this.instansi, tw: this.activeTw })
   },
   methods: {
      ...mapActions({
         getUnsurPelayanan: 'DashboardSkm/getUnsurPelayanan',
      }),
      async loadData(params) {
         this.loading = true
         try {
            await this.getUnsurPelayanan(params)
         } catch (error) {
            // Chart tetap menampilkan data kosong saat request gagal.
         } finally {
            this.loading = false
         }
      },
      unsurLabel(key) {
         const labels = {
            p1: 'Persyaratan Pelayanan',
            p2: 'Sistem, Mekanisme dan Prosedur',
            p3: 'Waktu Pelayanan',
            p4: 'Biaya/Tarif',
            p5: 'Produk Spesifikasi Jenis Pelayanan',
            p6: 'Kompetensi Pelaksana',
            p7: 'Perilaku Pelaksana',
            p8: 'Penanganan Pengaduan, Saran dan Masukan',
            p9: 'Sarana dan Prasarana',
         }

         return labels[key] || key.toUpperCase()
      },
   },
}
</script>

<template>
   <b-overlay
      :show="loading"
      :variant="isDark ? 'dark' : 'white'"
      spinner-variant="primary"
      blur="0"
      opacity=".75"
      rounded="sm"
   >
      <b-row>
         <b-col cols="12">
            <b-card
               class="card-wrapper"
               :class="{ 'card-wrapper-dark': isDark }"
            >
               <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-1">
                  <div class="mb-1 mb-md-0">
                     <h4 class="mb-25">
                        Analisis Unsur Pelayanan
                     </h4>
                     <small class="text-muted">Nilai rata-rata (NNR) per unsur pelayanan Tahun {{ tahun }}</small>
                  </div>
                  <div class="unsur-toolbar">
                     <label class="sr-only" for="skm-unsur-instansi">Instansi</label>
                     <v-select
                        id="skm-unsur-instansi"
                        v-model="instansi"
                        :options="instansiOptions"
                        :clearable="false"
                        :reduce="option => option"
                        placeholder="Pilih Instansi"
                     />
                  </div>
               </div>

               <div class="mb-1">
                  <b-tabs v-model="activeTab" pills>
                     <b-tab title="Triwulan I" />
                     <b-tab title="Triwulan II" />
                     <b-tab title="Triwulan III" />
                     <b-tab title="Triwulan IV" />
                  </b-tabs>
               </div>

               <vue-apex-charts
                  type="bar"
                  height="450"
                  :options="chartOptions"
                  :series="chartSeries"
               />
            </b-card>
         </b-col>
      </b-row>
   </b-overlay>
</template>

<style scoped>
.card-wrapper {
   border: 0;
   border-radius: 12px;
   box-shadow: 0 4px 24px rgba(34, 41, 47, 0.08);
   background-color: #ffffff;
}

.card-wrapper-dark {
   background-color: #283046;
   box-shadow: 0 4px 24px rgba(0, 0, 0, 0.25);
}

.unsur-toolbar {
   min-width: 260px;
   width: 100%;
}

@media (min-width: 768px) {
   .unsur-toolbar {
      width: 300px;
   }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
